import { useEffect, useMemo, useState } from "react";
import {
  login, adminMe, fetchAdminNotes, approveNote, deleteNote,
  fetchAdminBookings, cancelBooking, saveAvailability, saveContent,
  fetchContent, fetchAvailability, uploadFile,
  gcalStatus, gcalConnect, gcalDisconnect, fetchAnalytics,
} from "@/lib/api";
import { toast } from "sonner";
import { formatTime } from "@/lib/utils";
import { Trash2, Check, XCircle, LogOut, Upload } from "lucide-react";

const LINK_FIELDS = [
  ["safari", "Safari window URL"], ["substack", "Substack"], ["threads", "September Threads"],
  ["cashapp", "Cash App"], ["venmo", "Venmo"], ["paypal", "PayPal"],
  ["instagram", "Instagram"], ["x", "X (Twitter)"], ["linkedin", "LinkedIn"],
];
const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const ALL_SLOTS = Array.from({ length: 24 }, (_, h) => [`${String(h).padStart(2, "0")}:00`, `${String(h).padStart(2, "0")}:30`]).flat();

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await login({ email, password });
      localStorage.setItem("cs_token", r.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 p-4">
      <form onSubmit={submit} data-testid="admin-login-form"
        className="w-full max-w-sm p-8 rounded-2xl bg-neutral-900 border border-neutral-800 flex flex-col gap-4">
        <div className="text-center">
          <div className="text-xl font-bold text-white">corysuggs</div>
          <div className="text-sm text-neutral-400">Admin sign in</div>
        </div>
        <input data-testid="admin-email" type="email" required placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-base md:text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input data-testid="admin-password" type="password" required placeholder="Password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-base md:text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <button data-testid="admin-login-submit" disabled={busy}
          className="px-4 py-2.5 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition disabled:opacity-50">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function NotesTab() {
  const [notes, setNotes] = useState(null);
  const load = () => fetchAdminNotes().then(setNotes).catch((e) => toast.error("Could not load notes"));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await approveNote(id); toast.success("Approved"); load(); };
  const remove = async (id) => { await deleteNote(id); toast.success("Deleted"); load(); };

  if (!notes) return <div className="text-sm text-neutral-400">Loading…</div>;
  const pending = notes.filter((n) => n.status === "pending");
  const approved = notes.filter((n) => n.status === "approved");
  const priv = notes.filter((n) => n.is_private);

  const Card = ({ n, actions }) => (
    <div data-testid={`admin-note-${n.id}`} className="p-3 rounded-lg bg-neutral-800 border border-neutral-700">
      <div className="text-sm text-neutral-100 whitespace-pre-wrap">{n.message}</div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-xs text-neutral-400">— {n.name}{n.email ? ` · ${n.email}` : ""} · {formatTime(n.created_at)}</div>
        <div className="flex gap-1.5">{actions}</div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h3 className="text-sm font-semibold text-neutral-200 mb-2">Pending approval ({pending.length})</h3>
        <div className="flex flex-col gap-2">
          {pending.length === 0 && <div className="text-xs text-neutral-500">Nothing waiting.</div>}
          {pending.map((n) => (
            <Card key={n.id} n={n} actions={<>
              <button data-testid={`approve-${n.id}`} onClick={() => approve(n.id)} className="p-1.5 rounded bg-green-600/80 hover:bg-green-600 text-white"><Check size={14} /></button>
              <button data-testid={`delete-${n.id}`} onClick={() => remove(n.id)} className="p-1.5 rounded bg-red-600/80 hover:bg-red-600 text-white"><Trash2 size={14} /></button>
            </>} />
          ))}
        </div>
      </section>
      <section>
        <h3 className="text-sm font-semibold text-neutral-200 mb-2">Public testimonials ({approved.length})</h3>
        <div className="flex flex-col gap-2">
          {approved.length === 0 && <div className="text-xs text-neutral-500">None approved yet.</div>}
          {approved.map((n) => (
            <Card key={n.id} n={n} actions={
              <button data-testid={`delete-${n.id}`} onClick={() => remove(n.id)} className="p-1.5 rounded bg-red-600/80 hover:bg-red-600 text-white"><Trash2 size={14} /></button>
            } />
          ))}
        </div>
      </section>
      <section>
        <h3 className="text-sm font-semibold text-neutral-200 mb-2">Private notes ({priv.length})</h3>
        <div className="flex flex-col gap-2">
          {priv.length === 0 && <div className="text-xs text-neutral-500">No private notes.</div>}
          {priv.map((n) => (
            <Card key={n.id} n={n} actions={
              <button data-testid={`delete-${n.id}`} onClick={() => remove(n.id)} className="p-1.5 rounded bg-red-600/80 hover:bg-red-600 text-white"><Trash2 size={14} /></button>
            } />
          ))}
        </div>
      </section>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState(null);
  const [avail, setAvail] = useState(null);
  const [blockedInput, setBlockedInput] = useState("");
  const [gcal, setGcal] = useState(null);

  const load = () => {
    fetchAdminBookings().then(setBookings).catch(() => toast.error("Could not load bookings"));
    fetchAvailability().then((a) => setAvail({ ...a, weekly_hours: { ...a.weekly_hours } })).catch(() => {});
    gcalStatus().then(setGcal).catch(() => setGcal({ connected: false }));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("gcal") === "connected") {
      toast.success("Google Calendar connected");
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const connectGcal = async () => {
    try {
      const r = await gcalConnect();
      window.location.href = r.authorization_url;
    } catch {
      toast.error("Could not start Google connection");
    }
  };

  const disconnectGcal = async () => {
    await gcalDisconnect();
    toast.success("Google Calendar disconnected");
    load();
  };

  const toggleSlot = (day, slot) => {
    setAvail((a) => {
      const cur = new Set(a.weekly_hours[day] || []);
      cur.has(slot) ? cur.delete(slot) : cur.add(slot);
      return { ...a, weekly_hours: { ...a.weekly_hours, [day]: [...cur].sort() } };
    });
  };

  const save = async () => {
    try {
      await saveAvailability({
        weekly_hours: avail.weekly_hours,
        meeting_length: avail.meeting_length,
        timezone: avail.timezone,
        blocked_dates: avail.blocked_dates,
      });
      toast.success("Availability saved");
    } catch {
      toast.error("Could not save availability");
    }
  };

  const addBlocked = () => {
    if (!blockedInput) return;
    setAvail((a) => ({ ...a, blocked_dates: [...new Set([...a.blocked_dates, blockedInput])] }));
    setBlockedInput("");
  };

  const cancel = async (id) => {
    await cancelBooking(id);
    toast.success("Booking cancelled");
    load();
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="p-4 rounded-lg bg-neutral-800 border border-neutral-700 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-neutral-200">Google Calendar</h3>
          <div className="text-xs text-neutral-400 mt-0.5" data-testid="gcal-status">
            {gcal === null
              ? "Checking…"
              : gcal.connected
                ? `Connected as ${gcal.email} — new bookings appear on your calendar automatically.`
                : "Not connected. Connect it once and every booking lands on your calendar."}
          </div>
        </div>
        {gcal?.connected ? (
          <button data-testid="gcal-disconnect" onClick={disconnectGcal}
            className="px-3 py-1.5 rounded bg-neutral-700 text-neutral-200 text-xs hover:bg-neutral-600 shrink-0">
            Disconnect
          </button>
        ) : (
          <button data-testid="gcal-connect" onClick={connectGcal}
            className="px-3 py-1.5 rounded bg-sky-500 text-white text-xs font-semibold hover:bg-sky-600 shrink-0">
            Connect Google Calendar
          </button>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-neutral-200 mb-2">Upcoming meetings</h3>
        <div className="flex flex-col gap-2">
          {!bookings && <div className="text-xs text-neutral-500">Loading…</div>}
          {bookings?.filter((b) => b.status !== "cancelled").length === 0 && <div className="text-xs text-neutral-500">No upcoming meetings.</div>}
          {bookings?.filter((b) => b.status !== "cancelled").map((b) => (
            <div key={b.id} data-testid={`admin-booking-${b.id}`} className="p-3 rounded-lg bg-neutral-800 border border-neutral-700 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm text-neutral-100 font-medium">{b.name} — {b.date} at {b.time}</div>
                <div className="text-xs text-neutral-400">{b.email}{b.topic ? ` · ${b.topic}` : ""}</div>
              </div>
              <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancel(b.id)} className="p-1.5 rounded bg-red-600/80 hover:bg-red-600 text-white shrink-0"><XCircle size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      {avail && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-neutral-200">Weekly hours ({avail.timezone})</h3>
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-400">Length</label>
              <select data-testid="meeting-length" value={avail.meeting_length}
                onChange={(e) => setAvail({ ...avail, meeting_length: Number(e.target.value) })}
                className="px-2 py-1 rounded bg-neutral-800 border border-neutral-700 text-neutral-200 text-xs">
                {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-auto mac-scroll rounded-lg border border-neutral-800">
            <table className="text-xs w-full">
              <thead>
                <tr className="bg-neutral-800 text-neutral-300">
                  <th className="p-2 text-left">Time</th>
                  {DAYS.map((d) => <th key={d} className="p-2 capitalize">{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {ALL_SLOTS.map((slot) => (
                  <tr key={slot} className="border-t border-neutral-800">
                    <td className="p-1.5 text-neutral-400">{slot}</td>
                    {DAYS.map((d) => {
                      const on = (avail.weekly_hours[d] || []).includes(slot);
                      return (
                        <td key={d} className="p-1 text-center">
                          <button data-testid={`slot-${d}-${slot.replace(":", "")}`} onClick={() => toggleSlot(d, slot)}
                            className={`w-6 h-6 rounded ${on ? "bg-sky-500" : "bg-neutral-800 hover:bg-neutral-700"}`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input data-testid="blocked-date-input" type="date" value={blockedInput} onChange={(e) => setBlockedInput(e.target.value)}
              className="px-2 py-1.5 rounded bg-neutral-800 border border-neutral-700 text-neutral-200 text-xs" />
            <button data-testid="add-blocked-date" onClick={addBlocked} className="px-3 py-1.5 rounded bg-neutral-700 text-neutral-200 text-xs hover:bg-neutral-600">Block date</button>
            <div className="flex flex-wrap gap-1">
              {avail.blocked_dates.map((d) => (
                <span key={d} className="text-xs px-2 py-1 rounded bg-red-900/50 text-red-300 flex items-center gap-1">
                  {d}
                  <button onClick={() => setAvail((a) => ({ ...a, blocked_dates: a.blocked_dates.filter((x) => x !== d) }))}><Trash2 size={11} /></button>
                </span>
              ))}
            </div>
          </div>
          <button data-testid="save-availability" onClick={save}
            className="mt-3 px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition">
            Save availability
          </button>
        </section>
      )}
    </div>
  );
}

function ContentTab() {
  const [content, setContent] = useState(null);
  const [notifJson, setNotifJson] = useState("");
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    fetchContent().then((c) => {
      setContent(c);
      setNotifJson(JSON.stringify(c.lock_notifications || [], null, 2));
    });
  }, []);

  const set = (patch) => setContent((c) => ({ ...c, ...patch }));
  const setLink = (k, v) => setContent((c) => ({ ...c, links: { ...c.links, [k]: v } }));

  const save = async () => {
    try {
      let notifs;
      try {
        notifs = JSON.parse(notifJson);
      } catch {
        toast.error("Lock-screen notifications must be valid JSON");
        return;
      }
      await saveContent({
        tagline: content.tagline,
        mail_email: content.mail_email,
        links: content.links,
        lock_notifications: notifs,
        wallpaper_url: content.wallpaper_url,
        wallpaper_type: content.wallpaper_type,
        lock_photo_url: content.lock_photo_url,
      });
      toast.success("Content saved");
    } catch {
      toast.error("Could not save content");
    }
  };

  const upload = async (file, kind, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const r = await uploadFile(file, kind);
      set({ [field]: r.url });
      if (field === "wallpaper_url") set({ wallpaper_url: r.url, wallpaper_type: r.content_type.startsWith("video") ? "video" : "image" });
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading("");
    }
  };

  if (!content) return <div className="text-sm text-neutral-400">Loading…</div>;

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-neutral-200">Brand</h3>
        <label className="text-xs text-neutral-400">Tagline
          <input data-testid="content-tagline" value={content.tagline || ""} onChange={(e) => set({ tagline: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-100 text-base md:text-sm outline-none" />
        </label>
        <label className="text-xs text-neutral-400">Contact / Mail email
          <input data-testid="content-mail-email" value={content.mail_email || ""} onChange={(e) => set({ mail_email: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-100 text-base md:text-sm outline-none" />
        </label>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-neutral-200">Images</h3>
        {[["lock_photo_url", "Lock screen photo", "image"], ["wallpaper_url", "Desktop wallpaper (image or video)", "any"]].map(([field, label, kind]) => (
          <div key={field} className="flex items-center gap-3">
            <label className="flex-1 text-xs text-neutral-400">{label}
              <input data-testid={`content-${field}`} value={content[field] || ""} onChange={(e) => set({ [field]: e.target.value })} placeholder="Upload or paste URL"
                className="mt-1 w-full px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-100 text-base md:text-sm outline-none" />
            </label>
            <label data-testid={`upload-${field}`} className="mt-5 px-3 py-2 rounded-lg bg-neutral-700 text-neutral-200 text-xs cursor-pointer hover:bg-neutral-600 flex items-center gap-1.5">
              <Upload size={13} /> {uploading === field ? "Uploading…" : "Upload"}
              <input type="file" hidden accept={kind === "any" ? "image/*,video/*" : "image/*"}
                onChange={(e) => upload(e.target.files[0], kind, field)} />
            </label>
          </div>
        ))}
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-neutral-200">Dock links</h3>
        {LINK_FIELDS.map(([key, label]) => (
          <label key={key} className="text-xs text-neutral-400">{label}
            <input data-testid={`link-${key}`} value={content.links?.[key] || ""} onChange={(e) => setLink(key, e.target.value)}
              placeholder="https://…"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-100 text-base md:text-sm outline-none" />
          </label>
        ))}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-neutral-200">Lock-screen notifications</h3>
        <p className="text-xs text-neutral-500">Edit as JSON — each card has app, title and body.</p>
        <textarea data-testid="content-lock-notifs" rows={8} value={notifJson} onChange={(e) => setNotifJson(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-100 text-xs font-mono outline-none" />
      </section>

      <button data-testid="save-content" onClick={save}
        className="self-start px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition">
        Save content
      </button>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetchAnalytics().then(setData).catch(() => toast.error("Could not load analytics"));
  }, []);
  if (!data) return <div className="text-sm text-neutral-400">Loading…</div>;

  const maxViews = Math.max(1, ...data.daily.map((d) => d.views));
  const stats = [
    ["Today", data.today_views], ["Last 7 days", data.week_views],
    ["Last 30 days", data.month_views], ["All-time views", data.total_views],
    ["Unique visitors", data.total_uniques],
  ];
  return (
    <div className="flex flex-col gap-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map(([label, value]) => (
          <div key={label} className="p-4 rounded-lg bg-neutral-800 border border-neutral-700">
            <div className="text-2xl font-bold text-white" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
            <div className="text-xs text-neutral-400 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <section>
        <h3 className="text-sm font-semibold text-neutral-200 mb-3">Daily visits (last 30 days)</h3>
        {data.daily.length === 0 ? (
          <div className="text-xs text-neutral-500">No visits recorded yet.</div>
        ) : (
          <div className="flex items-end gap-1.5 h-36 p-3 rounded-lg bg-neutral-800 border border-neutral-700">
            {data.daily.map((d) => (
              <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group" title={`${d.date}: ${d.views} views, ${d.uniques} uniques`}>
                <div className="text-[10px] text-neutral-400 opacity-0 group-hover:opacity-100 transition">{d.views}</div>
                <div data-testid={`bar-${d.date}`} className="w-full rounded-t bg-sky-500/80 group-hover:bg-sky-400 transition"
                  style={{ height: `${Math.max(3, (d.views / maxViews) * 100)}%` }} />
              </div>
            ))}
          </div>
        )}
        {data.daily.length > 0 && (
          <div className="flex justify-between text-[10px] text-neutral-500 mt-1 px-1">
            <span>{data.daily[0].date}</span><span>{data.daily[data.daily.length - 1].date}</span>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-neutral-200 mb-2">Pages</h3>
        <div className="rounded-lg bg-neutral-800 border border-neutral-700 divide-y divide-neutral-700">
          {data.paths.length === 0 && <div className="p-3 text-xs text-neutral-500">No data yet.</div>}
          {data.paths.map((p) => (
            <div key={p.path} className="flex items-center justify-between px-3 py-2.5" data-testid={`path-${p.path.replace(/\//g, "-") || "root"}`}>
              <span className="text-sm text-neutral-200">{p.path === "/" ? "Home (lock screen + desktop)" : p.path}</span>
              <span className="text-sm text-neutral-400">{p.views} views</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">Unique visitors are counted per device/browser. No personal data is collected.</p>
      </section>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("notes");

  useEffect(() => {
    if (!localStorage.getItem("cs_token")) return setAuthed(false);
    adminMe().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className="min-h-screen bg-neutral-950" />;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const tabs = [["notes", "Notes"], ["bookings", "Bookings"], ["content", "Content"], ["analytics", "Analytics"]];
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 flex items-center gap-4 px-4 md:px-8 h-14 bg-neutral-900/90 border-b border-neutral-800" style={{ backdropFilter: "blur(12px)" }}>
        <div className="font-bold">corysuggs <span className="font-normal text-neutral-400">admin</span></div>
        <nav className="flex gap-1 ml-4">
          {tabs.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`px-3 py-1.5 rounded-lg text-sm transition ${tab === key ? "bg-neutral-800 text-white" : "text-neutral-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </nav>
        <div className="flex-1" />
        <button data-testid="admin-logout" onClick={() => { localStorage.removeItem("cs_token"); setAuthed(false); }}
          className="flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white">
          <LogOut size={14} /> Sign out
        </button>
      </header>
      <main className="max-w-4xl mx-auto p-4 md:p-8">
        {tab === "notes" && <NotesTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "content" && <ContentTab />}
        {tab === "analytics" && <AnalyticsTab />}
      </main>
    </div>
  );
}
